import styled, { StyledComponentPropsWithRef } from 'styled-components'
import { observer } from 'mobx-react'
import { ComponentProps, useEffect, useRef, forwardRef } from 'react'

import { preventDefaultLinkClickBehavior, type AppRoute } from '../../router'
import { theme } from '../../styles/theme'
import { SpinnerIcon } from '../Spinner/Spinner'
import { Label } from '../Typography/Typography'

import type { OperationStatus } from '../../models/common/OperationStatus'

export enum SortDirection {
  Asc = 'ascending',
  Desc = 'descending',
}

export const TableTitleRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  margin-block-end: 1em;
  grid-gap: 1em;
  text-transform: uppercase
`

const TableTitleLabel = styled(Label)`
  color: ${theme.colors.SecondaryGrey};
`

function getTableTitleText(entityCount: number, entityName: string): string {
  if (entityCount === 0) return `No ${entityName}s found`
  return `${entityCount} ${entityName}${entityCount > 1 ? 's' : ''}`
}

interface TableTitleProps {
  entityCount: number
  entityName: string
}
export const TableTitle = observer(({ entityCount, entityName, ...props }: TableTitleProps) => {
  return <TableTitleLabel {...props}>{getTableTitleText(entityCount, entityName)}</TableTitleLabel>
})

export const Table = styled.table`
  table-layout: fixed;

  inline-size: 100%;
  max-inline-size: 100%;
  word-break: break-word;
  color: ${theme.colors.body};
  font-size: ${theme.fontSizes.small};
  border-collapse: separate;
  border-spacing: 0;
  background-color: ${theme.colors.bodyBg};
`

const TableContainer = styled.div`
  block-size: 100%;
  max-block-size: 100%;
  overflow: auto;
`

export const ScrollableTable = forwardRef<HTMLDivElement, StyledComponentPropsWithRef<'div'>>(({ children, ...props }, ref) => (
  <TableContainer {...props} ref={ref} >
    <Table>
      {children}
    </Table>
  </TableContainer>
))

const PagedTableWrapper = styled.div`
  inline-size: 100%;
  block-size: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 2em;
`

interface PagedTableProps extends TableProps {
  lastFetchedPage: number
  numberOfPages: number
  loadNextPage: () => Promise<void>
  loadNextPageStatus: OperationStatus
}

export const PagedTable = observer(
  ({ lastFetchedPage, numberOfPages, loadNextPage, loadNextPageStatus, children, ...tableProps }: PagedTableProps) => {
    const tableContainerRef = useRef<HTMLDivElement>(null)

    const loadNextPageIfNeeded = () => {
      if (lastFetchedPage >= numberOfPages) {
        return
      }
      const wrapperDiv = tableContainerRef.current
      if (!wrapperDiv) {
        return
      }

      //  https://medium.com/@jbbpatel94/difference-between-offsetheight-clientheight-and-scrollheight-cfea5c196937
      const wrapperScrolledToBottom = wrapperDiv.scrollHeight - (wrapperDiv.scrollTop + wrapperDiv.clientHeight) <= 0
      if (wrapperScrolledToBottom && !loadNextPageStatus.inProgress) {
        void loadNextPage()
      }
    }

    useEffect(loadNextPageIfNeeded, [lastFetchedPage]) // Load next page immediately if needed (maybe there's more place on the screen)

    // TODO: also call loadNextPageIfNeeded if the window, or container div is resized.
    return (
      <PagedTableWrapper onScroll={loadNextPageIfNeeded} ref={tableContainerRef}>
        <Table {...tableProps}>{children}</Table>
        {loadNextPageStatus.inProgress && <SpinnerIcon title="Loading..." />}
      </PagedTableWrapper>
    )
  },
)

export type TableType = typeof Table
export type TableProps = StyledComponentPropsWithRef<TableType> 

export const Thead = styled.thead`
  font-weight: ${theme.fontWeights.bold};
`

export const Tbody = styled.tbody``

interface StyledTableRowProps extends MaybeWithIsDanger, MaybeWithIsSelected {
  isSelectable?: boolean
  backgroundColor?: string
}

export const Tr = styled.tr<StyledTableRowProps>`
  transition: all ${theme.transitions.normal} ease-out;
  position: relative;
  cursor: ${({ isSelectable }) => (isSelectable ? 'pointer' : 'inherit')};
  background-color: ${({ isDanger = false, isSelected = false, backgroundColor }) => 
    backgroundColor ? `${backgroundColor}${isSelected ? 'FF':'7F'}` : // If backgroundColor supplied, use isSelected to modify opacity
    isSelected ? theme.colors.recordSelected : 
    isDanger ? theme.colors.dangerBg : 
    'inherit'};
  :target,
  :hover {
    background-color: ${({ isDanger = false, isSelected = false, isSelectable = false, backgroundColor }) => 
      backgroundColor ? `${backgroundColor}FF` : 
      isSelected ? theme.colors.recordSelected : 
      isDanger ? theme.colors.dangerBg : 
      isSelectable ? theme.colors.recordHover :
      'inherit'
    };
  }

  :last-child > td {
    border-block-end: ${theme.borders.table};
  }
  :not(:first-child) > td {
    border-block-start: ${theme.borders.table};
  }
  & > td {
    :first-child {
      border-inline-start: ${theme.borders.table};
    }
    :last-child {
      border-inline-end: ${theme.borders.table};
    }
    &[class] {
      /* Higher specificity required to override the selectors above. */
      /* stylelint-disable-next-line max-nesting-depth -- Nesting is required here. */
      &,
      :first-child,
      :last-child {
        border-color: ${({ isDanger = false }) => (isDanger ? theme.colors.danger : theme.colors.MediumGrey)};
      }
    }
  }
`

export const ExtendedViewTr = styled(Tr)`
  &[class] > td {
    border-block-start: none;
  }
`

const ClickableTr = styled(Tr)`
  cursor: pointer;
`

interface TrWithLinkProps extends StyledTableRowProps {
  route: AppRoute
}

export const TrWithLink = ({route, ...props}: TrWithLinkProps) => <ClickableTr {...{
  onClick: () => { 
    // If the click was made as part of an attempt to copy text, disregard it. from https://stackoverflow.com/questions/31982407/prevent-onclick-event-when-selecting-text
    const selectedText = document.getSelection() 
    if (selectedText?.type === 'Range') {
      return
    }
    route.push()
  },
  ...props
}} />

export interface TrProps extends ComponentProps<typeof Tr> {}

const TdAnchorForTrLinkStyled = styled.a`
  :hover {
    color: currentColor;
  }
  ::before {
    content: '';
    position: absolute;
    inset: 0;
  }
`

// AppRoute is a union, which forces us to use the `type` syntax here instead of `interface`.
// https://stackoverflow.com/a/56085307/915875
type TdAnchorForTrLinkProps = Partial<AppRoute> & StyledComponentPropsWithRef<typeof TdAnchorForTrLinkStyled>

export function TdAnchorForTrLink({ push, onClick = push, href, ...props }: TdAnchorForTrLinkProps) {
  return (
    <TdAnchorForTrLinkStyled
      {...{
        href,
        onClick(e) {
          onClick?.(e)
          if (preventDefaultLinkClickBehavior(e) && push) {
            push()
          }
        },
        ...props,
      }}
    />
  )
}

interface TdProps extends ComponentProps<'td'> {
  textAlign?: 'start' | 'end'
  shouldWrap?: boolean
}

export const Td = styled.td<TdProps>`
  font-variant-numeric: tabular-nums;
  vertical-align: middle;
  word-wrap: break-word;
  white-space: normal;
  text-align: ${({ textAlign = 'start' }) => textAlign};
  padding: 1em 0 1em 1em;
  :last-child {
    padding: 1em;
  }
`

export const ActionsTd = styled(Td)`
  text-align: end;
  /* To allow displaying the menu outside the td box */
  overflow: visible;

  /* A new stacking context in order for the button to be above the anchor of a 'TableRowLink' */
  button {
    position: relative;
    ::before {
      content: '';
      inset: 0;
    }
  }
`

export const Th = styled(Td)`
  text-transform: uppercase;
  color: ${theme.colors.Gray7};

  /* Sticky headers */
  border-block: ${theme.borders.table};
  position: sticky;
  inset-block-start: 0;
  background-color: ${theme.colors.White};
  z-index: 2;
`
export const WrappingTh = styled(Th)`
  white-space: normal;
`

interface WithSortedByAndDirection {
  sortKey: string
  sortedBy: string
  sortDirection: SortDirection
}

interface ThSortableProps extends StyledComponentPropsWithRef<typeof Th>, WithSortedByAndDirection, MaybeWithIsDisabled {
  onClick: () => void
}

const ThSortButton = styled.button<WithSortedByAndDirection>`
  inline-size: 100%;
  position: relative;
  padding-inline-end: ${({ sortKey, sortedBy }) => (sortKey === sortedBy ? '2ch' : '0')};
  color: ${({ sortKey, sortedBy }) => (sortKey === sortedBy ? theme.colors.GreenPrimary : 'currentColor')};
  ::after {
    content: '⏶';
    display: ${({ sortKey, sortedBy }) => (sortKey === sortedBy ? 'initial' : 'none')};
    transform: ${({ sortDirection }) => (sortDirection === SortDirection.Desc ? 'rotate(180deg)' : 'none')};
    margin-inline-start: 0.5ch;
    pointer-events: none;
    color: ${theme.colors.Gray7};
    /* This is really weird: the CSS variables don't work here without absolute positioning. */
    position: absolute;
  }
`

export function ThSortable({
  children,
  onClick,
  sortKey,
  sortedBy,
  sortDirection,
  title = sortKey === sortedBy ? `Sorted by ${sortedBy}. Click to reverse order.` : `Sort by ${sortKey}`,
  isDisabled = false,
  ...props
}: ThSortableProps) {
  return (
    <Th {...props}>
      <ThSortButton
        {...{
          type: 'button',
          onClick,
          sortKey,
          sortedBy,
          sortDirection,
          title,
          children,
          isDisabled,
        }}
      />
    </Th>
  )
}

export const ActionsTh = styled(Th)``

export const TableBoldText = styled.span`
  font-weight: ${theme.fontWeights.bold};
`
interface MaybeWithGap {
  gap?: string;
}

export const Column = styled.div<MaybeWithGap>`
  display: flex;
  gap: ${({gap}) => (gap || '0.6em')};
  flex-direction: column;
`

export const CollapsibleColumn = styled(Column)`
  min-block-size: 0;
  overflow: auto;
`

export const EllipsisColumn = styled(CollapsibleColumn)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  > * {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`

export const Row = styled.div<MaybeWithGap>`
  display: flex;
  gap: ${({gap}) => (gap || '0.6em')};
  flex-direction: row;

  align-items: center;
`

export const EllipsisRow = styled(Row)`
  > ${EllipsisColumn} {
    min-inline-size: 0;
  }
`
